import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCircle2, Save, Mail, IdCard, Sparkles, Shield, Loader2, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * AccountPage — self-serve profile editor.
 *
 * Compact by design: this is NOT a full settings hub. Only the two
 * user-editable identity fields (Display Name + Preferred First Name /
 * Alias) plus read-only role/email/linked-contact info. Everything the
 * backend accepts flows through `authApi.updateProfile` — a member and an
 * admin hit the same endpoint; the JWT decides whose profile is edited.
 */
export default function AccountPage() {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [preferredAlias, setPreferredAlias] = useState("");
  const [initialDisplay, setInitialDisplay] = useState("");
  const [initialAlias, setInitialAlias] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authApi.getProfile();
        if (cancelled) return;
        setProfile(data);
        setDisplayName(data.display_name || data.name || "");
        setPreferredAlias(data.preferred_alias || "");
        setInitialDisplay(data.display_name || data.name || "");
        setInitialAlias(data.preferred_alias || "");
      } catch {
        toast.error("Could not load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = displayName.trim() !== initialDisplay.trim() || preferredAlias.trim() !== initialAlias.trim();

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await authApi.updateProfile({
        display_name: displayName.trim(),
        preferred_alias: preferredAlias.trim(),
      });
      setProfile(data);
      setInitialDisplay(data.display_name || "");
      setInitialAlias(data.preferred_alias || "");
      // Sync the auth cache so the sidebar badge, login header etc pick up the new name.
      if (refreshUser) await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save profile";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const platformRole = profile?.platform_role || user?.platform_role || user?.role || "member";
  const isAdmin = platformRole === "admin";

  return (
    <div className="h-full overflow-y-auto" data-testid="account-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 md:py-10 pb-24 md:pb-16">
        <div className="mb-6 md:mb-8">
          <div className="text-eyebrow mb-1">Account</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Your profile</h1>
          <p className="text-white/50 mt-2 text-sm">
            How you appear across HQ. Your preferred first name is what natural-language commands like
            <span className="text-white/70"> "assign to {previewAlias(preferredAlias || initialAlias) || "Nate"}"</span> resolve to.
          </p>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-6">
            {/* Editable identity */}
            <section
              className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
              data-testid="account-identity-section"
            >
              <div className="px-5 pt-5 pb-4 border-b border-white/5 flex items-center gap-2">
                <UserCircle2 className="w-4 h-4 text-white/60" />
                <div className="text-eyebrow">Identity</div>
              </div>
              <div className="p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-eyebrow" htmlFor="display-name">Display name</label>
                  <Input
                    id="display-name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    maxLength={80}
                    placeholder="e.g. Nathaniel Broughton"
                    data-testid="account-display-name-input"
                    className="bg-white/5 border-white/10 text-white h-11"
                  />
                  <div className="text-[11px] text-white/40">
                    Shown in HQ, the sidebar, the digest, and every task/note author label.
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-eyebrow" htmlFor="preferred-alias">Preferred first name / Alias</label>
                  <Input
                    id="preferred-alias"
                    value={preferredAlias}
                    onChange={(e) => setPreferredAlias(e.target.value)}
                    maxLength={40}
                    placeholder="e.g. Nate"
                    data-testid="account-alias-input"
                    className="bg-white/5 border-white/10 text-white h-11"
                  />
                  <div className="text-[11px] text-white/40 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Used by Quick Add + Executive Assistant when you say "assign to&nbsp;
                    <span className="text-white/70">{previewAlias(preferredAlias) || "Nate"}</span>".
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid="account-save-btn"
                    className="rounded-full bg-white text-black hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Save className="w-4 h-4 mr-1.5" />
                    {saving ? "Saving…" : "Save changes"}
                  </Button>
                  {dirty && !saving && (
                    <span className="text-[11px] text-amber-200/80" data-testid="account-dirty-hint">
                      Unsaved changes
                    </span>
                  )}
                </div>
              </div>
            </section>

            {/* Read-only account facts */}
            <section
              className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
              data-testid="account-facts-section"
            >
              <div className="px-5 pt-5 pb-4 border-b border-white/5 flex items-center gap-2">
                <IdCard className="w-4 h-4 text-white/60" />
                <div className="text-eyebrow">Account</div>
              </div>
              <ul className="divide-y divide-white/5">
                <FactRow
                  icon={Mail}
                  label="Email"
                  value={profile.email || "—"}
                  testid="account-email-row"
                  hint="Contact the workspace admin to change your login email."
                />
                <FactRow
                  icon={Shield}
                  label="Role"
                  value={platformRole === "admin" ? "Executive admin" : "Team member"}
                  testid="account-role-row"
                />
                {profile.linked_contact && (
                  <li className="px-5 py-3 flex items-center gap-3" data-testid="account-linked-contact-row">
                    <UserCircle2 className="w-4 h-4 text-white/50 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-white/40 uppercase tracking-wider">Linked contact</div>
                      <div className="text-sm text-white/90 truncate">{profile.linked_contact.full_name}</div>
                    </div>
                    <Link
                      to={`/contacts/${profile.linked_contact.id}`}
                      data-testid="account-linked-contact-link"
                      className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors inline-flex items-center gap-1"
                    >
                      Open <ChevronRight className="w-3 h-3" />
                    </Link>
                  </li>
                )}
              </ul>
              <div className="px-5 py-3 border-t border-white/5 flex items-center gap-2 flex-wrap">
                <Link
                  to="/settings/password"
                  data-testid="account-change-password-link"
                  className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors"
                >
                  Change password
                </Link>
                {isAdmin && (
                  <Link
                    to="/team"
                    data-testid="account-team-link"
                    className="text-[11px] uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 bg-white/[0.04] text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors"
                  >
                    Manage team
                  </Link>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function previewAlias(raw) {
  return (raw || "").trim().slice(0, 40);
}

function FactRow({ icon: Icon, label, value, hint, testid }) {
  return (
    <li className="px-5 py-3 flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 text-white/50 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-white/40 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-white/90 break-all">{value}</div>
        {hint && <div className="text-[11px] text-white/40 mt-1">{hint}</div>}
      </div>
    </li>
  );
}
